import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Timer, TrendingUp, HeartPulse } from "lucide-react";
import { api } from "@/api";
import { RiskBadge, initials } from "@/lib/risk";

const COLUMNS = [
  { key: "Needs Contact", tint: "#EF4444" },
  { key: "Contacted", tint: "#F59E0B" },
  { key: "Intervention Scheduled", tint: "#4F46E5" },
  { key: "Monitoring", tint: "#0EA5E9" },
  { key: "Recovered", tint: "#10B981" },
];

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5 card-shadow" data-testid={`iv-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${accent}18` }}>
        <Icon className="h-4.5 w-4.5" style={{ color: accent }} />
      </span>
      <div className="mt-3 font-display text-2xl font-bold text-ink">{value}</div>
      <div className="text-sm text-ink-muted">{label}</div>
    </div>
  );
}

export default function Interventions() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.interventions().then(setData).catch(() => {}); }, []);

  if (!data) return <div className="py-24 text-center text-ink-muted">Loading intervention center…</div>;
  const m = data.metrics;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">Intervention Center</h1>
        <p className="text-ink-muted">Track every rescue from first contact to recovery.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric icon={HeartPulse} label="Active Interventions" value={m.active_interventions} accent="#4F46E5" />
        <Metric icon={Timer} label="Avg Response Time" value={m.avg_response_time} accent="#0EA5E9" />
        <Metric icon={TrendingUp} label="Recovery Success" value={m.recovery_success} accent="#10B981" />
        <Metric icon={Clock} label="Recovered This Month" value={m.recovered_this_month} accent="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {COLUMNS.map((col, ci) => {
          const items = data.board[col.key] || [];
          return (
            <motion.div key={col.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: ci * 0.05 }} className="rounded-2xl bg-secondary/50 p-3"
              data-testid={`iv-column-${col.key.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="mb-3 flex items-center justify-between px-1">
                <span className="flex items-center gap-2 text-sm font-bold text-ink">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.tint }} />
                  {col.key}
                </span>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-ink-muted">{items.length}</span>
              </div>
              <div className="space-y-2.5">
                {items.map((s) => (
                  <button key={s.id} onClick={() => nav(`/students/${s.id}`)}
                    data-testid={`iv-card-${s.id}`}
                    className="w-full rounded-xl border border-border bg-white p-3 text-left card-shadow transition-transform hover:-translate-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-bold text-ink">{initials(s.name)}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-ink">{s.name}</div>
                        <div className="text-xs text-ink-muted">Risk {s.risk_score}</div>
                      </div>
                    </div>
                    {s.interventions?.length > 0 && (
                      <div className="mt-2 rounded-lg bg-indigo-light/70 px-2 py-1 text-xs font-medium text-indigo-brand">
                        {s.interventions[s.interventions.length - 1].type} · {s.interventions[s.interventions.length - 1].date}
                      </div>
                    )}
                    <div className="mt-2"><RiskBadge level={s.risk_level} size="sm" /></div>
                  </button>
                ))}
                {items.length === 0 && (
                  <div className="rounded-xl border border-dashed border-border py-6 text-center text-xs text-ink-muted">No students</div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
